import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Dropdown, List, SearchBox } from 'tea-component';
import moment from 'moment';
import { getLocale, useIntl } from 'umi';
import { DBO_TM } from '@op/utils/format';
import './index.scss';

type DateType = 'daily' | 'monthly' | 'hourly';

const leftFormatListMap: { [key in DateType]: string } = {
  daily: 'YYYY-MM',
  monthly: 'YYYY',
  hourly: 'YYYY-MM-DD',
};

const rightFormatListMap: { [key in DateType]: string } = {
  daily: 'YYYY-MM-DD HH:mm',
  monthly: 'MM',
  hourly: 'YYYY-MM-DD HH:mm',
};

const titleFormatListMap: { [key in DateType]: string } = {
  daily: 'YYYY-MM-DD',
  monthly: 'YYYY-MM',
  hourly: 'YYYY-MM-DD HH:mm',
};

const titleText: {
  [key in DateType]: {
    left: string;
    right: string;
  };
} = {
  daily: {
    left: 'Month',
    right: 'Day',
  },
  monthly: {
    left: 'Year',
    right: 'Month',
  },
  hourly: {
    left: '',
    right: 'Hour',
  },
};

// eslint-disable-next-line react/display-name
export default forwardRef(
  (
    props: {
      handleReportChange?: (value: any) => void;
      handleInit?: (value: any) => void;
      reportInsightConfig: any[];
      type: DateType;
      defaultValue?: any;
    },
    ref?: React.Ref<any>,
  ) => {
    const {
      handleReportChange: onChange,
      handleInit,
      reportInsightConfig,
      type,
      defaultValue: defaultValueProps,
    } = props;

    const defaultValue = useMemo(
      () => defaultValueProps ?? {},
      [defaultValueProps],
    );
    const [year, setYear] = useState('');
    const [groups, setGroups] = useState([]);
    const [cur, setCur] = useState<any>({});
    const [search, setSearch] = useState('');
    const [data, setData] = useState<any[]>([]);
    const language = getLocale();
    useEffect(() => {
      setData(reportInsightConfig || []);
    }, [reportInsightConfig]);

    const { options, renderOptions } = useMemo(() => {
      const leftOptionsMap: any = {};
      for (const item of data) {
        const { start_time, end_time } = item;
        const leftFormat = leftFormatListMap[type];
        const titleFormat = titleFormatListMap[type];
        const rightFormat = rightFormatListMap[type];

        const title = `${moment(start_time).format(titleFormat)} ~ ${moment(
          end_time,
        ).format(titleFormat)}`;

        const leftKey = moment(start_time).format(leftFormat);
        let startTime = moment(start_time).format(rightFormat);
        const endTime = moment(end_time).format(rightFormat);
        if (startTime !== endTime) {
          startTime = `${startTime} ~ ${endTime}`;
        }

        // 使用精确时间戳作为唯一标识，避免天粒度下不同时间段 id 相同的问题
        const uniqueId = `${start_time} ~ ${end_time}`;

        if (search) {
          if (!leftKey.includes(search) && !title.includes(search)) {
            continue;
          }
        }

        const obj = { ...item, title: startTime, id: uniqueId };
        if (!leftOptionsMap[leftKey]) {
          leftOptionsMap[leftKey] = [obj];
        } else {
          leftOptionsMap[leftKey].push(obj);
        }
      }

      const yearMapEntity = Object.entries(leftOptionsMap).sort(
        ([date1], [date2]) => {
          const flag = DBO_TM.isAfter(date1, date2);

          return flag ? -1 : 1;
        },
      );

      const options: any[] = [];
      const renderOptions: any[] = [];
      let splitKey = '';
      yearMapEntity.forEach(([key, children]) => {
        ((children as Array<any>) || [])?.sort((a, b) => {
          const flag = DBO_TM.isAfter(a.start_time, b.start_time);
          return flag ? -1 : 1;
        });
        if (type === 'hourly') {
          const newKey = moment(key).format('YYYY-MM');
          if (splitKey !== newKey) {
            renderOptions.push({
              title: newKey,
              isGroup: true,
            });
            splitKey = newKey;
          }
        }
        const data: any = {
          title: key,
          children,
        };
        options.push(data);
        renderOptions.push(data);
      });
      return { options, renderOptions };
    }, [data, search]);

    const handleYearClick = (data: any) => {
      setYear(data.title);
      setGroups(data.children || []);
    };

    const handleReportChange = (child: any, close: { (): void; (): void }) => {
      onChange?.(child);
      setCur(child);
      close();
    };

    useEffect(() => {
      if (Array.isArray(options) && options.length > 0) {
        if (!defaultValue?.start_time || !defaultValue?.end_time) {
          handleRecentReport();
        } else {
          const { start_time, end_time } = defaultValue;
          const curYear = moment(start_time).format(leftFormatListMap[type]);
          const { children: curGroups } =
            options?.find((val) => val.title === curYear) || {};
          const curChild = curGroups?.find(
            (val: any) =>
              val.start_time === start_time && val.end_time === end_time,
          );
          if (curChild) {
            setYear(curYear);
            setGroups(curGroups);
            setCur(curChild);
            handleInit?.(curChild);
          }
        }
      }
    }, [options, defaultValue]);

    const handleSearchClear = () => {};
    const handleSearchChange = (value: string) => {
      setSearch(value.trim());
    };
    const { formatMessage } = useIntl();
    useEffect(() => {
      setGroups(options?.[0]?.groups || []);
    }, [search]);

    const { left, right } = titleText[type];

    const handleRecentReport = (defaultValue: any = {}) => {
      if (!defaultValue?.start_time || !defaultValue?.end_time) {
        const filteredOptions =
          options?.filter((val) => {
            const matchedChildren = val.children?.filter(
              (child: { status?: string }) =>
                child.status === 'success' ||
                (child.status === undefined && child.status !== ''),
            );
            return matchedChildren?.length > 0;
          }) || [];
        const item = filteredOptions?.[0] || {};
        const curYear = item.title;
        const curGroups =
          item.children?.filter(
            (child: { status?: string }) =>
              child.status === 'success' ||
              (child.status === undefined && child.status !== ''),
          ) || [];

        setYear(curYear);
        setGroups(curGroups);
        setCur(curGroups?.[0] || {});
        handleInit?.(curGroups?.[0] || {});
      } else {
        const { start_time, end_time } = defaultValue;
        const curYear = moment(start_time).format(leftFormatListMap[type]);
        const { children: curGroups } =
          options?.find((val) => val.title === curYear) || {};
        const curChild = curGroups?.find(
          (val: any) =>
            val.start_time === start_time && val.end_time === end_time,
        );
        if (curChild) {
          setYear(curYear);
          setGroups(curGroups);
          setCur(curChild);
          handleInit?.(curChild);
        }
      }
    };

    const validReport = useMemo(() => {
      const filteredOptions =
        options?.filter((val) => {
          const matchedChildren = val.children?.filter(
            (child: { status?: string }) =>
              child.status === 'success' || !child.status,
          );
          return matchedChildren?.length > 0;
        }) || [];
      return filteredOptions.length > 0;
    }, [options]);

    useImperativeHandle(ref, () => ({
      handleRecentReport,
      validReport,
    }));

    return (
      <Dropdown
        className="dbo-report-select"
        destroyOnClose={false}
        button={
          <>
            <span className="select-button-text">
              {formatMessage({ id: 'Report' })}
            </span>
            <span className="select-button-value">
              {language === 'zh-CN' ? cur?.title : cur?.title_en || cur?.title}
            </span>
          </>
        }
        appearance="button"
        clickClose={false}
        boxClassName="dbo-report-select-container dbo-multiple-select"
      >
        {(close) => (
          <>
            <SearchBox
              placeholder={formatMessage({ id: 'Search' })}
              onChange={handleSearchChange}
              onClear={handleSearchClear}
            />
            <div className="dbo-report-select-content-container">
              <div className="dbo-report-select-content-wrap">
                <List type="option-group">
                  {type !== 'hourly' && (
                    <div className="dbo-report-select-content-title-wrap">
                      <span>{formatMessage({ id: left })}</span>
                    </div>
                  )}
                  {renderOptions.map((item) => {
                    if (item.isGroup) {
                      return (
                        <div
                          key={item.title}
                          className="dbo-report-select-content-title-wrap"
                        >
                          <span>{item.title}</span>
                        </div>
                      );
                    }
                    return (
                      <List.SubMenu
                        label={item.title}
                        key={item.title}
                        onClick={() => handleYearClick(item)}
                        className={`dbo-report-dropdown-item ${
                          item.title === year ? 'active' : ''
                        }`}
                      ></List.SubMenu>
                    );
                  })}
                </List>
              </div>

              <div className="dbo-report-select-content-wrap">
                <List type="option-group">
                  <div className="dbo-report-select-content-title-wrap">
                    <span>{formatMessage({ id: right })}</span>
                  </div>
                  {groups?.map((child: { title: string; id: string }) => (
                    <List.Item
                      key={child.id + child.title}
                      className={`dbo-report-dropdown-item dbo-report-dropdown-singleitem ${
                        child.id === cur?.id ? 'active' : ''
                      }`}
                      onClick={() => handleReportChange(child, close)}
                    >
                      {child.title}
                    </List.Item>
                  ))}
                </List>
              </div>
            </div>
          </>
        )}
      </Dropdown>
    );
  },
);
